import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoChevronLeft } from "react-icons/go";
import ReactQuill from "react-quill";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import { FiImage } from "react-icons/fi";
import { useMutation } from "@apollo/client";
import { CREATE_BLOG, UPDATE_BLOG } from "../../Utils/Mutation";
import Loader from "../Common/Loader";
import { useSnackbarHook } from "../../hooks/useSnackbarHook";
import { uploadSingleImageToCloudinary } from "../../Utils/productEditHelpers";

const initialFormState = {
  title: "",
  slug: "",
  imageUrl: "",
  content: "",
  category: "",
  description:""
};

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

const CreateBlog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const createdPreviewUrlRef = useRef(null);
  const initialBlog = location.state?.blog || null;
  const isEditMode = Boolean(id || initialBlog?._id);

  const [formState, setFormState] = useState(initialFormState);
  const [coverImageName, setCoverImageName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [formError, setFormError] = useState("");
  const [hasEditedSlug, setHasEditedSlug] = useState(false);

  const { handleClick } = useSnackbarHook();

  const [createBlog, { loading: isCreating }] = useMutation(CREATE_BLOG);
  const [updateBlog, { loading: isUpdating }] = useMutation(UPDATE_BLOG);

  useEffect(() => {
    if (!initialBlog) return;

    setFormState({
      title: initialBlog.title || "",
      slug: initialBlog.slug || "",
      imageUrl: initialBlog.imageUrl || "",
      content: initialBlog.content || "",
      category: initialBlog.category || "",
      description:initialBlog.description || "",
    });
    setPreviewUrl(initialBlog.imageUrl || "");
    setCoverImageName(initialBlog.imageUrl ? "Current blog cover" : "");
    setHasEditedSlug(Boolean(initialBlog.slug));
  }, [initialBlog]);

  useEffect(() => {
    return () => {
      if (createdPreviewUrlRef.current) {
        URL.revokeObjectURL(createdPreviewUrlRef.current);
      }
    };
  }, []);

  const wordCount = useMemo(() => {
    const plainText = formState.content
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!plainText) return 0;
    return plainText.split(" ").length;
  }, [formState.content]);

  const readingTime = useMemo(() => {
    const minutes = Math.max(1, Math.ceil(wordCount / 200));
    return `~${minutes} min read`;
  }, [wordCount]);

  const isSubmitDisabled = useMemo(
    () =>
      isUploading ||
      isCreating ||
      isUpdating ||
      !formState.title.trim() ||
      !formState.slug.trim() ||
      !formState.imageUrl ||
      !formState.content.trim() ||
      !formState.category,
    [formState, isCreating, isUpdating, isUploading],
  );

  const updateFormField = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (!hasEditedSlug) {
      setFormState((prev) => ({
        ...prev,
        slug: slugify(prev.title),
      }));
    }
  }, [formState.title, hasEditedSlug]);

  const handleFileSelection = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFormError("Please select a valid image file.");
      handleClick("Please select a valid image file.", "error");
      return;
    }

    setFormError("");
    setCoverImageName(file.name);

    if (createdPreviewUrlRef.current) {
      URL.revokeObjectURL(createdPreviewUrlRef.current);
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    createdPreviewUrlRef.current = nextPreviewUrl;
    setPreviewUrl(nextPreviewUrl);

    setIsUploading(true);
    try {
      const uploadedUrl = await uploadSingleImageToCloudinary(file);
      updateFormField("imageUrl", uploadedUrl);
      handleClick("Image uploaded successfully", "success");
    } catch (err) {
      setFormError(err?.message || "Image upload failed.");
      updateFormField("imageUrl", "");
      handleClick(err?.message || "Image upload failed.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCreateBlog = async (status) => {
    setFormError("");

    if (!formState.title.trim()) {
      setFormError("Title is required.");
      return;
    }

    if (!formState.slug.trim()) {
      setFormError("Slug is required.");
      return;
    }

    if (!formState.imageUrl) {
      setFormError("Please upload a cover image before submitting.");
      return;
    }

    const plainContent = formState.content.replace(/<[^>]*>/g, "").trim();
    if (!plainContent) {
      setFormError("Content is required.");
      return;
    }

    if (!formState.category) {
      setFormError("Please select a category.");
      return;
    }

    try {
      const variables = {
        title: formState.title.trim(),
        slug: formState.slug.trim(),
        imageUrl: formState.imageUrl,
        content: formState.content,
        category: formState.category,
        status,
        description:formState.description
      };

      if (isEditMode) {
        await updateBlog({
          variables: {
            blogId: id || initialBlog?._id,
            ...variables,
          },
        });
      } else {
        await createBlog({
          variables,
        });
      }

      handleClick(
        isEditMode
          ? "Blog updated successfully"
          : status === "unpublished"
            ? "Blog saved as draft successfully"
            : "Blog published successfully",
        "success",
      );
      navigate("/dashboard/blog-management");
    } catch (err) {
      const message = err?.message || "Failed to create blog.";
      setFormError(message);
      handleClick(message, "error");
    }
  };

  return (
    <div>
      {(isCreating || isUpdating || isUploading) && <Loader />}
      <div className="flex items-center gap-x-4">
        <Link to="/dashboard/blog-management">
          <IconButton sx={{ bgcolor: "#fff", borderRadius: "8px" }}>
            <GoChevronLeft className="text-[24px]" />
          </IconButton>
        </Link>
        <div>
          <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
            {isEditMode ? "Edit Post" : "Create New Post"}
          </Typography>
          <Typography sx={{ fontSize: "12px" }}>
            {isEditMode
              ? "Update the details below"
              : "Fill in the details below"}
          </Typography>
        </div>
      </div>

      <div className="mt-5 bg-white p-5">
        <Box sx={{ width: { lg: "70%", md: "80%", xs: "100%" } }}>
          <InputLabel>Title*</InputLabel>
          <TextField
            margin="dense"
            fullWidth
            value={formState.title}
            onChange={(e) => updateFormField("title", e.target.value)}
            InputProps={{
              style: {
                borderRadius: "8px",
              },
            }}
          />
          <div className="mt-3">
            <InputLabel>Slug*</InputLabel>
            <TextField
              margin="dense"
              fullWidth
              value={formState.slug}
              onChange={(e) => {
                setHasEditedSlug(true);
                updateFormField("slug", slugify(e.target.value));
              }}
              InputProps={{
                style: {
                  borderRadius: "8px",
                },
              }}
            />
          </div>
          <div className="mt-3">
               <InputLabel>Brief Description*</InputLabel>
          <TextField
            margin="dense"
            fullWidth
            multiline
            minRows={4}
            value={formState.description}
            onChange={(e) => updateFormField("description", e.target.value)}
            InputProps={{
              style: {
                borderRadius: "8px",
              },
            }}
          />
          </div>
          <div className="mt-3">
            <InputLabel>Cover Image*</InputLabel>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              style={{ display: "none" }}
              onChange={handleFileSelection}
            />
            <div
              className="mt-2 flex h-[120px] cursor-pointer flex-col items-center justify-center rounded-[8px] border border-dashed border-[#d2d2d2] bg-[#fafafa] px-4 text-center"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-[#f3eaf6]">
                {isUploading ? (
                  <CircularProgress size={18} sx={{ color: "#7B408C" }} />
                ) : (
                  <FiImage className="text-[18px] text-[#7B408C]" />
                )}
              </div>
              <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
                {isUploading
                  ? "Uploading image..."
                  : coverImageName || "Click to upload cover image"}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#777" }}>
                PNG, JPEG upto 10MB
              </Typography>
            </div>

            {(previewUrl || formState.imageUrl) && (
              <Box sx={{ mt: 2 }}>
                <Box
                  component="img"
                  src={previewUrl || formState.imageUrl}
                  alt={coverImageName || formState.title || "Cover preview"}
                  sx={{
                    width: "100%",
                    maxHeight: 220,
                    objectFit: "cover",
                    borderRadius: "10px",
                    border: "1px solid #E5E5E5",
                  }}
                />
              </Box>
            )}
          </div>
          <div className="mt-3">
            <InputLabel>Content*</InputLabel>
            <div className="mt-2">
              <ReactQuill
                className="editor"
                theme="snow"
                value={formState.content}
                onChange={(value) => updateFormField("content", value)}
              />
            </div>
            <Typography sx={{ mt: 1, fontSize: "12px", color: "#777" }}>
              {readingTime} • {wordCount} words
            </Typography>
          </div>

          <div className="mt-3">
            <InputLabel>Category*</InputLabel>
            <div className="mt-2">
              <TextField
                value={formState.category}
                onChange={(e) => updateFormField("category", e.target.value)}
                select
                fullWidth
              >
                <MenuItem value="" disabled>
                  Select Category
                </MenuItem>
                <MenuItem value="Seller/Vendor">Seller/Vendor</MenuItem>
                <MenuItem value="Buyer/Customer">Buyer/Customer</MenuItem>
              </TextField>
            </div>
          </div>

          {formError && (
            <Typography sx={{ mt: 2, color: "#D32F2F", fontSize: "13px" }}>
              {formError}
            </Typography>
          )}

          <div className="mt-10 flex items-center justify-end gap-x-4">
            <Button
              variant="outlined"
              sx={{ height: "45px", width: "150px", borderRadius: "50px" }}
              disabled={isSubmitDisabled}
              onClick={() => handleCreateBlog("unpublished")}
            >
              {isEditMode ? "Save Changes" : "Save as Draft"}
            </Button>
            <Button
              variant="contained"
              sx={{ height: "45px", width: "150px", borderRadius: "50px" }}
              disabled={isSubmitDisabled}
              onClick={() => handleCreateBlog("published")}
            >
              {isEditMode ? "Update & Publish" : "Publish Now"}
            </Button>
          </div>
        </Box>
      </div>
    </div>
  );
};

export default CreateBlog;
